import { useState } from "react";
import { Rainbow, Users, Compass, ChevronDown, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { api } from "@/lib/api";
import ExplorePrefsDialog from "@/components/ExplorePrefsDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * SidePill — persistent header pill showing the viewer's active side
 * (LGBTQ / Straight) with a one-tap switcher underneath. Mounted in both
 * MobileHeader (top of screen on phones) and LeftRail (desktop) so every
 * page in the app answers "which side am I on right now?" without a trip
 * to Profile > Edit.
 *
 * Hides itself before the user has picked a side (the mandatory
 * SidePickerModal is already blocking the UI in that case, so a redundant
 * pill would only add visual noise).
 */
const SIDE_META = {
  lgbtq:    { label: "LGBTQ",    Icon: Rainbow, color: "text-rose-400 border-rose-500/40 hover:border-rose-500" },
  straight: { label: "Straight", Icon: Users,   color: "text-sky-300 border-sky-400/40 hover:border-sky-400" },
  explore:  { label: "Explore",  Icon: Compass, color: "text-amber-300 border-amber-400/40 hover:border-amber-400" },
};

export default function SidePill({ compact = false, className = "" }) {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);
  if (!user || !user.side) return null;

  const current = SIDE_META[user.side];
  if (!current) return null;
  const { Icon } = current;

  const switchTo = async (next) => {
    if (next === user.side || busy) return;
    setBusy(true);
    try {
      await api.post("/users/me/side", { side: next });
      await refresh();
      window.dispatchEvent(new Event("pandax:refresh-feed"));
      toast.success(
        next === "lgbtq" ? "Switched to the LGBTQ side 🌈"
          : next === "explore" ? "Explore Your Way — you now see both sides."
          : "Switched to the Straight side."
      );
      if (next === "explore") setPrefsOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch sides.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={busy}
          data-testid="side-pill"
          data-side={user.side}
          className={`inline-flex items-center gap-1.5 border px-2.5 py-1.5 text-xs font-medium bg-black/40 transition-colors disabled:opacity-60 ${current.color} ${className}`}
        >
          <Icon size={12} />
          {!compact && <span data-testid="side-pill-label">{current.label} side</span>}
          <ChevronDown size={11} className="opacity-70" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="bg-black border border-white/10 rounded-none min-w-[220px]"
        data-testid="side-pill-menu"
      >
        <DropdownMenuLabel className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 px-3 py-2">
          Switch side
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-white/5" />
        {Object.entries(SIDE_META).map(([key, meta]) => {
          const Ico = meta.Icon;
          const active = user.side === key;
          return (
            <DropdownMenuItem
              key={key}
              onClick={() => switchTo(key)}
              disabled={busy}
              data-testid={`side-pill-option-${key}`}
              className={`flex items-center justify-between gap-3 px-3 py-2.5 cursor-pointer rounded-none focus:bg-white/5 ${active ? "text-white" : "text-zinc-400 hover:text-white"}`}
            >
              <span className="flex items-center gap-2">
                <Ico size={14} className={meta.color.split(" ")[0]} />
                {meta.label} side
              </span>
              {active && <Check size={13} className="text-rose-400" />}
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator className="bg-white/5" />
        {user.side === "explore" && (
          <DropdownMenuItem onClick={() => setPrefsOpen(true)} data-testid="side-pill-explore-prefs" className="px-3 py-2.5 cursor-pointer rounded-none text-amber-200 focus:bg-white/5">
            Explore preferences…
          </DropdownMenuItem>
        )}
        <div className="px-3 py-2 text-[10px] text-zinc-600 leading-relaxed">
          LGBTQ and Straight stay inside one audience. Explore shows both and lets you filter who you meet.
        </div>
      </DropdownMenuContent>
      <ExplorePrefsDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
    </DropdownMenu>
  );
}
